package gr.tech.pdfgenerator.pages

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.forms.Button
import com.varabyte.kobweb.silk.components.icons.fa.FaArrowLeft
import com.varabyte.kobweb.silk.components.icons.fa.FaFloppyDisk
import com.varabyte.kobweb.silk.components.layout.HorizontalDivider
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import gr.tech.pdfgenerator.components.generation.PDFGenerationUI
import gr.tech.pdfgenerator.components.layouts.AuthPageLayout
import gr.tech.pdfgenerator.components.layouts.PDFLayout
import gr.tech.pdfgenerator.infrastructure.api.savePDFWithProjectID
import gr.tech.pdfgenerator.infrastructure.data.GeneratorData
import gr.tech.pdfgenerator.infrastructure.pdf.GeneratePDFTool
import gr.tech.pdfgenerator.infrastructure.pdf.PDFGenStatus
import gr.tech.pdfgenerator.models.user.USER_ROLES
import gr.tech.pdfgenerator.sections.summaryLine
import gr.tech.pdfgenerator.sections.updatePDFCostWaitingGraph
import gr.tech.pdfgenerator.sections.updatePDFCummulCashFlowGraph
import gr.tech.pdfgenerator.sections.updatePDFSavingsBarGraph
import gr.tech.pdfgenerator.styles.*
import gr.tech.pdfgenerator.util.*
import gr.tech.pdfgenerator.util.CONSTANTS.FONT_FAMILY2
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.AlignItems
import org.jetbrains.compose.web.dom.*
import kotlin.math.roundToLong

@Page("lightingproposal")
@Composable
fun proposalGeneratePage() {
    AuthPageLayout(
        title = "Inputs",
        requiredRoles = listOf(USER_ROLES.CONFIRMED_USER.role)
    ) {
        proposalGenerateScreen()
    }
}

@Composable
fun proposalGenerateScreen() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()

    val generator = GeneratePDFTool()
    var generationStatus: PDFGenStatus by remember { mutableStateOf(PDFGenStatus.GENERATING) }

    val projectTitleFormatted = GeneratorData.generalInputsState.value.projectName.replace(" ", "")
    val documentTitle = "${projectTitleFormatted}-${getCurrentDateString()}"

    val pageNumbers = mutableMapOf<String, Int>()
    var currentPage = 1

    pageNumbers["title"] = currentPage++
    pageNumbers["summary"] = currentPage++
    pageNumbers["cashflow"] = currentPage++
    pageNumbers["costwaiting"] = currentPage++
    pageNumbers["savings"] = currentPage++

    if (GeneratorData.propInputState.value.toggleFinancePage) {
        pageNumbers["finance"] = currentPage++
    }
    if (GeneratorData.propInputState.value.toggleLeasingPage) {
        pageNumbers["leasing"] = currentPage++
    }

    pageNumbers["environment"] = currentPage++
    pageNumbers["formulas"] = currentPage++
//    pageNumbers["termsconditions"] = currentPage++

    val pageTotal = pageNumbers.size

    LaunchedEffect(Unit) {
        if (GeneratorData.project.value.id != null) {
            generator.genPDF(
                onGenerationStatus = { status ->
                    generationStatus = status
                }
            )
        } else {
            ctx.router.tryRoutingTo(BACKEND.ROUTES.PROJECTS)
        }
    }

    /*
        -------------- PDF UI --------------

        Everything below here is the UI and the generated PDF page.
        Please no fancy calculations or logic down here, try to keep it UI only.

        TODO THIS PAGE IS NOT UP TO DATE WITH NEW RULSET

        Some rules I tried to follow:
        - TODO Margin, Padding, one-off styles were allowed in-line.
        - TODO For styles that require more than one property, turn it into a style.
        - TODO Keep Columns, Rows, and function headers on a one line, so that collapsing for human readability/debugging is nicer.

        There are a lot of lines below, so trying to keep things to a minimum in terms of length for this section.

        ---------------------------------------
     */

    PDFLayout(documentTitle) {

        PDFGenerationUI(
            generator = generator,
            status = generationStatus,
            onGoBack = {
                ctx.router.tryRoutingTo("/inputs?projectid=${GeneratorData.project.value.id}")
            },
            onStatusUpdate = { generationStatus = it },
            onSave = { blob ->
                scope.launch {
                    generationStatus = PDFGenStatus.DOWNLOADING
                    if (GeneratorData.project.value.id != null) {
                        savePDFWithProjectID(
                            projectId = GeneratorData.project.value.id!!,
                            projectName = documentTitle,
                            projectType = CONSTANTS.PROJECT_TYPES.LIGHTING_PROPOSAL,
                            pdfBlob = blob
                        )
                    }
                    generationStatus = PDFGenStatus.DOWNLOADED
                }
            }
        )

        // Page to Print
        Div(ElementToPrintStyle.toAttrs {
            id("element-to-print")
        }) {

            // Page 1: Title
            Div(pageStyle.toModifier().toAttrs()) {
                Img(RES.PROJECT_PROPOSAL.PAGE_1)
            }

            // Page 2: Summary
            Div(pageStyle.toModifier().toAttrs()) {
                Img(RES.PROJECT_PROPOSAL.PAGE_2, alt = "Summary title", backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    Row(Modifier.margin(top = 215.px).fillMaxWidth().height(138.px).backgroundColor(Theme.PDFNearBackground.rgb).padding(-1.px)) {
                        Column(summaryColumnStyle.toModifier(), horizontalAlignment = Alignment.Start) {
                            pdfSummaryBox("Net Project Cost",
                                formatCurrency(GeneratorData.propCalculationsState.value.projectCost)
                            )
                            pdfSummaryBox("Rebate", formatCurrency(GeneratorData.propCalculationsState.value.rebates))
                        }
                        Column(summaryColumnStyle.toModifier(), horizontalAlignment = Alignment.Start) {
                            pdfSummaryBox("Annual Savings",
                                formatCurrency(GeneratorData.propCalculationsState.value.totalAnnualSavings)
                            )
                            pdfSummaryBox("Total Savings Over 10 Years",
                                formatCurrency(GeneratorData.propCalculationsState.value.cummCashFlowList.last())
                            )
                        }
                        Column(summaryColumnStyle.toModifier(), horizontalAlignment = Alignment.Start) {
                            pdfSummaryBox("Annualized ROI",  if (GeneratorData.propCalculationsState.value.annualizedROI > 0) "${(GeneratorData.propCalculationsState.value.annualizedROI * 100).roundToLong()}%" else "0%")
                            pdfSummaryBox("Simple Payback", if (GeneratorData.propCalculationsState.value.simplePayback > 0) "${GeneratorData.propCalculationsState.value.simplePayback.roundToOneDecimalPlace()} Years" else "0 Years")
                        }
                    }

                    Div(Modifier.margin(top = 26.px).toAttrs()) {
                        summarySectionTitle("return on investment (ROI)")
                        Div(
                            Modifier.margin(topBottom = 8.px).padding(leftRight = 55.px).color(Theme.PDFNearBackground.rgb).fontWeight(
                            FontWeight.Bold).toAttrs()) {
                            summaryLine("Total Return on Investment (ROI)", if (GeneratorData.propCalculationsState.value.totalROI > 0) "${(GeneratorData.propCalculationsState.value.totalROI * 100).roundToTwoDecimalPlace()}%" else "0%")
                            summaryLine("Annualized ROI (10 years)", if (GeneratorData.propCalculationsState.value.annualizedROI > 0) "${(GeneratorData.propCalculationsState.value.annualizedROI * 100).roundToLong()}%" else "0%")
                            summaryLine("Simple Payback", if (GeneratorData.propCalculationsState.value.simplePayback > 0) "${GeneratorData.propCalculationsState.value.simplePayback.roundToOneDecimalPlace()} Years" else "0 Years")
                        }

                        summarySectionTitle("project investment")
                        Div(Modifier.margin(topBottom = 8.px).padding(leftRight = 55.px).toAttrs()) {
                            summaryLine("Replacement Fixtures/Bulbs",
                                formatCurrency(GeneratorData.propCalculationsState.value.replaceFixBulbs)
                            )
                            summaryLine("Project Cost",
                                formatCurrency(GeneratorData.propCalculationsState.value.projectCost), Modifier.fontWeight(
                                FontWeight.Bold))
                            summaryLine("Total Project Cost",
                                formatCurrency(GeneratorData.propCalculationsState.value.totalProjectCost)
                            )
                            summaryLine("Rebates", if (GeneratorData.propCalculationsState.value.rebates > 0) "-${
                                formatCurrency(
                                    GeneratorData.propCalculationsState.value.rebates
                                )
                            }" else "$0") //appending negative to it, as it is being subtracted.
                            summaryLine("Net Project Cost",
                                formatCurrency(GeneratorData.propCalculationsState.value.netProjectCost), Modifier.fontWeight(
                                FontWeight.Bold))
                        }

                        summarySectionTitle("cost savings")
                        Div(Modifier.margin(topBottom = 8.px).padding(leftRight = 55.px).toAttrs()) {
                            summaryLine("Annual Energy Savings (Lighting)",
                                formatCurrency(GeneratorData.propCalculationsState.value.energySavingsLighting)
                            )
                            summaryLine("Annual Energy Savings (HVAC)",
                                formatCurrency(GeneratorData.propCalculationsState.value.energySavingsHVAC)
                            )
                            summaryLine("Annual Maintenance Savings",
                                formatCurrency(GeneratorData.propCalculationsState.value.maintenanceSavings)
                            )
                            summaryLine("Total Annual Savings",
                                formatCurrency(GeneratorData.propCalculationsState.value.totalAnnualSavings), Modifier.fontWeight(FontWeight.Bold))
                            summaryLine("Total Savings Over 10 years",
                                formatCurrency(GeneratorData.propCalculationsState.value.cummCashFlowList.last()), Modifier.fontWeight(FontWeight.Bold))
                        }

                        summarySectionTitle("energy savings")
                        Div(Modifier.margin(topBottom = 8.px).padding(leftRight = 55.px).toAttrs()) {
                            summaryLine("Existing Energy Usage", "${formatNumber(GeneratorData.propInputState.value.baselineKWH.toString())} kWh")
                            summaryLine("Proposed Energy Usage", "${formatNumber(GeneratorData.propInputState.value.proposedKWH.toString())} kWh")
                            summaryLine("Energy Savings", "${formatNumber(GeneratorData.derivationsState.value.annualKWHSaved.toString())} kWh")

                            val energyPercent = (GeneratorData.derivationsState.value.annualKWHSaved.toDouble() / GeneratorData.propInputState.value.baselineKWH.toDouble()).format(2).toDouble() //I hate this. Too many toDouble(), but for now it works?
                            summaryLine("Energy Reduction", if (energyPercent > 0) "${(energyPercent * 100)}%" else "0%", Modifier.fontWeight(
                                FontWeight.Bold))
                        }
                    }
                    pageFooter(pageNumbers["summary"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                }
            }

            // Page 3: Cash Flow Analysis
            Div(pageStyle.toModifier().toAttrs()) {
                Img(RES.PROJECT_PROPOSAL.PAGE_3, alt = "Cash Flow title", backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    Column(Modifier.margin(top= 215.px), horizontalAlignment = Alignment.CenterHorizontally) {
                        Label(attrs = Modifier.color(Colors.Grey).margin(top = 5.px, bottom = 10.px).fontSize(10.px).toAttrs()) {
                            Text("10 Year Cash Flow - Assumes annual utility escalation rate of 3%")
                        }
                        Table(Modifier.toAttrs{
                            style {
                                property("border-collapse", "collapse")
                            }
                        }) {
                            Tr {
                                Td(ProjectProposalGreyBackStyle.toModifier().then(pdfLabelCellStyle.toModifier()).toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text("Year")
                                    }
                                }
                                repeat(10) { columnIndex ->
                                    Td(ProjectProposalGreyBackStyle.toModifier().textAlign(TextAlign.Center).fontSize(10.px).toAttrs()) {
                                        Span(tableBoxPaddingStyle.toAttrs()) {
                                            Text("${columnIndex + 1}")
                                        }
                                    }
                                }
                            }
                            Tr {
                                Td(ProjectProposalGreyBackStyle.toModifier().then(pdfLabelCellStyle.toModifier()).toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text("Project Cost")
                                    }
                                }
                                Td(pdfCellStyle.toModifier().toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text(formatCurrency(GeneratorData.propCalculationsState.value.projectCost.roundCents()))
                                    }
                                }
                                repeat(9) {
                                    Td(pdfCellStyle.toModifier().textAlign(TextAlign.End).toAttrs()) {
                                        Span(tableBoxPaddingStyle.toAttrs()) {
                                            Text("-")
                                        }
                                    }
                                }
                            }
                            Tr {
                                Td(ProjectProposalGreyBackStyle.toModifier().then(pdfLabelCellStyle.toModifier()).toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text("Energy Savings")
                                    }
                                }
                                GeneratorData.propCalculationsState.value.energySavingList.forEachIndexed { _, amount ->
                                    Td(pdfCellStyle.toModifier().toAttrs()) {
                                        Span(tableBoxPaddingStyle.toAttrs()) {
                                            Text(formatCurrency(amount.roundCents()))
                                        }
                                    }
                                }
                            }
                            Tr {
                                Td(ProjectProposalGreyBackStyle.toModifier().then(pdfLabelCellStyle.toModifier()).toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text("HVAC Savings")
                                    }
                                }
                                GeneratorData.propCalculationsState.value.hvacSavingsList.forEachIndexed { _, amount ->
                                    Td(pdfCellStyle.toModifier().toAttrs()) {
                                        Span(tableBoxPaddingStyle.toAttrs()) {
                                            Text(formatCurrency(amount.roundCents()))
                                        }
                                    }
                                }
                            }
                            Tr {
                                Td(ProjectProposalGreyBackStyle.toModifier().then(pdfLabelCellStyle.toModifier()).toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text("Maint Savings")
                                    }
                                }
                                repeat(10) {
                                    Td(pdfCellStyle.toModifier().toAttrs()) {
                                        Span(tableBoxPaddingStyle.toAttrs()) {
                                            Text(formatCurrency(GeneratorData.derivationsState.value.maintenanceSavings))
                                        }
                                    }
                                }
                            }
                            Tr {
                                Td(ProjectProposalGreyBackStyle.toModifier().then(pdfLabelCellStyle.toModifier().margin(top = 20.px)).toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text("Net Cash Flow")
                                    }
                                }
                                GeneratorData.propCalculationsState.value.netCashFlowList.forEachIndexed { _, amount ->
                                    Td(pdfCellStyle.toModifier().toAttrs()) {
                                        Span(tableBoxPaddingStyle.toAttrs()) {
                                            Text(formatCurrency(amount.roundCents()))
                                        }
                                    }
                                }
                            }
                            Tr {
                                Td(ProjectProposalGreyBackStyle.toModifier().then(pdfLabelCellStyle.toModifier()).toAttrs()) {
                                    Span(tableBoxPaddingStyle.toAttrs()) {
                                        Text("Cum Cash Flow")
                                    }
                                }
                                GeneratorData.propCalculationsState.value.cummCashFlowList.forEachIndexed { _, amount ->
                                    Td(pdfCellStyle.toModifier().toAttrs()) {
                                        Span(tableBoxPaddingStyle.toAttrs()) {
                                            Text(formatCurrency(amount.roundCents()))
                                        }
                                    }
                                }
                            }
                        }


                        Div(
                            Modifier.fillMaxWidth().display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).justifyContent(
                            JustifyContent.Center).alignItems(AlignItems.Center).margin(top = 60.px).toAttrs {
                            id("pdfcummulflow-graph")
                        }) {
                            Label(attrs = Modifier.color(Colors.Grey).fontSize(10.px).toAttrs()) {
                                Text("Cumulative Cash Flow Chart")
                            }

                            LaunchedEffect(GeneratorData.propCalculationsState.value.cummCashFlowList.toMutableList()) {
                                updatePDFCummulCashFlowGraph(GeneratorData.propCalculationsState.value.cummCashFlowList.toMutableList())
                            }
                        }
                    }
                    pageFooter(pageNumbers["cashflow"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                }
            }

            // Page 4: Cost of Waiting
            Div(pageStyle.toModifier().toAttrs()) {
                Img(RES.PROJECT_PROPOSAL.PAGE_4, alt = "Cash Waiting title", backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    Div(
                        Modifier.margin(top = 300.px).fillMaxWidth().display(DisplayStyle.Flex).flexDirection(
                            FlexDirection.Column).justifyContent(
                        JustifyContent.Center).alignItems(AlignItems.Center).toAttrs {
                        id("pdfcostWaiting-graph")
                    }) {
                        LaunchedEffect(GeneratorData.propCalculationsState.value.costInYearsList) {
                            updatePDFCostWaitingGraph(GeneratorData.propCalculationsState.value.costInYearsList.toMutableList())
                        }
                    }

                    pageFooter(pageNumbers["costwaiting"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                }
            }

            // Page 5: Savings Analysis
            Div(pageStyle.toModifier().toAttrs()) {
                Img(RES.PROJECT_PROPOSAL.PAGE_5, alt = "Savings Analysis title", backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    Div(savingsBubbleStyle.toModifier().top(335.px).left(40.px).toAttrs()) {
                        Span(savingsBubbleTextStyle.toModifier().fontSize(38.px).toAttrs()) {
                            Text(formatCurrency(GeneratorData.propCalculationsState.value.annualSavings.roundCents()))
                        }
                    }
                    Div(savingsBubbleStyle.toModifier().top(375.px).left(300.px).toAttrs()) {
                        Span(savingsBubbleTextStyle.toModifier().fontSize(38.px).toAttrs()) {
                            Text("${(GeneratorData.propCalculationsState.value.ledCostSavings * 100).roundToLong()}%")
                        }
                    }
                    Div(
                        Modifier.margin(top = 450.px, bottom = 50.px)
                        .display(DisplayStyle.Flex)
                        .justifyContent(JustifyContent.Center)
                        .fillMaxWidth().toAttrs()) {
                        Div(Modifier.margin(topBottom = 50.px).toAttrs {
                            id("pdfSavingsBar-graph")
                        }) {
                            LaunchedEffect(
                                GeneratorData.propCalculationsState.value.baselineEnergyCosts,
                                GeneratorData.propCalculationsState.value.ledEnergyCosts,
                                GeneratorData.propCalculationsState.value.baselineMaintenance,
                                GeneratorData.propCalculationsState.value.baselineHVAC,
                                GeneratorData.propCalculationsState.value.ledHVAC,
                                GeneratorData.propCalculationsState.value.baselineTotal,
                                GeneratorData.propCalculationsState.value.ledTotal
                            ) {
                                updatePDFSavingsBarGraph(
                                    GeneratorData.propCalculationsState.value.baselineEnergyCosts,
                                    GeneratorData.propCalculationsState.value.ledEnergyCosts,
                                    GeneratorData.propCalculationsState.value.baselineMaintenance,
                                    GeneratorData.propCalculationsState.value.baselineHVAC,
                                    GeneratorData.propCalculationsState.value.ledHVAC,
                                    GeneratorData.propCalculationsState.value.baselineTotal,
                                    GeneratorData.propCalculationsState.value.ledTotal
                                )
                            }
                        }
                    }
                    pageFooter(pageNumbers["savings"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                }
            }

            // Page 6: Financing
            //TODO create Financing page with values.
            if (GeneratorData.propInputState.value.toggleFinancePage) {
                Div(pageStyle.toModifier().toAttrs()) {
                    Img(RES.PROJECT_PROPOSAL.PAGE_6, alt = "Financing title", backgroundStyle.toAttrs())
                    Div(overlayTextStyle.toAttrs()) {
                        pageFooter(pageNumbers["finance"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                    }
                }
            }

            // Page 7: Leasing
            //TODO create Leasing page with values.
            if (GeneratorData.propInputState.value.toggleLeasingPage) {
                Div(pageStyle.toModifier().toAttrs()) {
                    Img(RES.PROJECT_PROPOSAL.PAGE_7, alt = "Leasing title", backgroundStyle.toAttrs())
                    Div(overlayTextStyle.toAttrs()) {
                        pageFooter(pageNumbers["leasing"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                    }
                }
            }

            // Page Environmental Impact
            Div(pageStyle.toModifier().toAttrs()) {
                Img(RES.PROJECT_PROPOSAL.PAGE_8, alt = "Environment Impact", backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    Div(envImpactBubbleStyle.toModifier().top(280.px).left(100.px).toAttrs()) {
                        Span(envImpactTextStyle.toModifier().fontSize(80.px).color(Theme.PDFDarkBackground.rgb).toAttrs()) {
                            Text("${if (GeneratorData.propCalculationsState.value.co2ReducedPercentage > 0) (GeneratorData.propCalculationsState.value.co2ReducedPercentage * 100).roundToLong() else 0}%")
                        }
                    }
                    Div(envImpactBubbleStyle.toModifier().top(430.px).left(385.px).toAttrs()) {
                        Span(envImpactTextStyle.toModifier().fontSize(80.px).toAttrs()) {
                            Text(GeneratorData.propCalculationsState.value.treesPlanted.toString())
                        }
                    }
                    Div(envImpactBubbleStyle.toModifier().top(590.px).left(465.px).toAttrs()) {
                        Span(envImpactTextStyle.toModifier().fontSize(80.px).color(Theme.PDFDarkBackground.rgb).toAttrs()) {
                            Text(GeneratorData.propCalculationsState.value.carsRemoved.toString())
                        }
                    }
                    pageFooter(pageNumbers["environment"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                }
            }

            // Page Formulas
            Div(pageStyle.toModifier().toAttrs()) {
                Img(RES.PROJECT_PROPOSAL.PAGE_9, alt = "Formulas title", backgroundStyle.toAttrs())
                Div(overlayTextStyle.toAttrs()) {
                    Div(
                        Modifier.margin(top = 215.px, leftRight = 55.px).display(DisplayStyle.Flex).flexDirection(
                            FlexDirection.Column).toAttrs()) {
                        formulaSectionTitle("General Assumptions")
                        formulaLine("kWh Rate", "$0.10")
                        formulaLine("Annual kWh Rate Increase", "3.0%")
                        formulaLine("Cooling Months for HVAC Savings", "12")
                        formulaLine("HVAC Coefficient", "0.33")
                    }
                    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).margin(top = 20.px, leftRight = 55.px).toAttrs()) {
                        formulaSectionTitle("Energy Savings")
                        formulaLine("Total Existing Watts", "Number of Fixtures × Number of Watts")
                        formulaLine("Total New Watts", "Number of New Fixtures × Number of Watts")
                        formulaLine("Energy Saved Watts", "Total Existing Watts - Total New Watts")
                        formulaLine("% Energy Savings", "Energy Saved Watts ÷ Total Existing Watts")
                    }
                    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).margin(top = 20.px, leftRight = 55.px).toAttrs()) {
                        formulaSectionTitle("Maintenance Savings")
                        formulaLine("Existing Lamp Replcmts/Year (ELRY)", "Operating Hours ÷ Existing Lamp Life")
                        formulaLine("New Lamp Replcmts/Year (NLRY)", "Operating Hours ÷ New Lamp Life")
                        formulaLine("Annual Maintenance Savings", "(ELRY - NLRY) × Existing Lamp Cost × Existing Lamps/Fixt × Existing Fixtures")
                    }
                    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).margin(top = 20.px, leftRight = 55.px).toAttrs()) {
                        formulaSectionTitle("HVAC Savings")
                        formulaLine("HVAC Savings", "Energy Saved Watts ÷ 1000 × Oper Hrs × (Cooling Mos ÷ 12) × HVAC Coef × kWh Rate")
                    }
                    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).margin(top = 20.px, leftRight = 55.px).toAttrs()) {
                        formulaSectionTitle("Financial Savings")
                        formulaLine("Existing Energy Cost", "Existing Fixture Watts × Qty Fixtures ÷ 1000 × kWh Rate")
                        formulaLine("Annual Proposed Energy Cost", "New Fixture Watts × Qty Fixtures ÷ 1000 × kWh Rate")
                        formulaLine("Annual Energy Savings", "Existing Energy Cost - Annual Proposed Energy Cost")
                    }
                    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).margin(top = 20.px, leftRight = 55.px).toAttrs()) {
                        formulaSectionTitle("Rebates")
                        formulaLine("Total Fixture Rebate", "Quantity of Fixtures × Per Fixture Rebate Amount")
                        formulaLine("kW Rebate", "Energy Saved Watts ÷ 1000 × kW Rebate")
                        formulaLine("kWh Rebate", "Energy Saved Watts ÷ 1000 × Oper Hrs × kWh Rebate")
                    }
                    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).margin(top = 20.px, leftRight = 55.px).toAttrs()) {
                        formulaSectionTitle("Project Economics")
                        formulaLine("Payback (Years)", "Project Cost ÷ Annual Energy Savings")
                        formulaLine("Total ROI %", "Total Savings ÷ Project Cost")
                        formulaLine("Annualized ROI %", "Total ROI ÷ 5 Years")
                    }
                    pageFooter(pageNumbers["formulas"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
                }
            }

            // Page Terms and Conditions
//            Div(pageStyle.toModifier().toAttrs()) {
//                Img(RES.PROJECT_PROPOSAL.PAGE_10, alt = "Terms & Conditions title", backgroundStyle.toAttrs())
//                Div(overlayTextStyle.toAttrs()) {
//                    Div(
//                        Modifier.margin(top = 215.px, left = 100.px, right = 160.px)
//                        .display(DisplayStyle.Flex)
//                        .flexDirection(FlexDirection.Column)
//                        .justifyContent(JustifyContent.Center)
//                        .alignItems(AlignItems.Start)
//                        .fontSize(12.px)
//                        .lineHeight(1.cssRem).toAttrs()) {
//
//                        P(Modifier.fontWeight(FontWeight.Bold).margin(0.px).toAttrs()) {
//                            Text("Scope of Work and Payment Terms")
//                        }
//                        P(Modifier.margin(0.px).toAttrs()) {
//                            Span(Modifier.fontWeight(FontWeight.Bold).margin(0.px).toAttrs()) {
//                                Text("a. Rebate Procurement. ")
//                            }
//                            Text("Green Rebates will, on the Customer's behalf, contact and communicate\n" +
//                                    "with the Customer's electricity provider, ETO (\"Service Provider\"), in order to procure rebates\n" +
//                                    "and incentives in relation to the \"Efficiency Measures\" listed below. Green Rebates will prepare and\n" +
//                                    "submit all forms and documents to Service Provider necessary for rebate approval. Green Rebates\n" +
//                                    "will coordinate with representatives of Service Provider and Customer for any site visits,\n" +
//                                    "inspections, or monitoring necessary for the completion of the rebate project. Green Rebates will\n" +
//                                    "work with Customer to ensure the implementation of the efficiency measures required for the\n" +
//                                    "approved incentive.")
//                        }
//
////                        P(Modifier.margin(0.px).toAttrs()) {
////                            Span(Modifier.fontWeight(FontWeight.Bold).margin(0.px).toAttrs()) {
////                                Text("b. Efficiency Measures.")
////                            }
////                            Text("The following Efficiency Measures, include, but are not limited to: Retrofit, lighting - 464x Enlite TL660, 120 Enlite FLORA520")
////                        }
//
//                        P(Modifier.margin(0.px).toAttrs()) {
//                            Span(Modifier.fontWeight(FontWeight.Bold).margin(0.px).toAttrs()) {
//                                Text("b. Customer Responsibility for Incentive Payment Reduction. ")
//                            }
//                            Text("Customer is responsible for\n" +
//                                    "compensating Green Rebates for any reduction in the incentive payment that is a result of the\n" +
//                                    "Customer's failure to comply with the terms of the Incentive Agreement with Service Provider,\n" +
//                                    "in the amount of the difference between the approved incentive payment and the final payment\n" +
//                                    "from Service Provider.")
//                        }
//
//                        P(Modifier.margin(0.px).toAttrs()) {
//                            Span(Modifier.fontWeight(FontWeight.Bold).margin(0.px).toAttrs()) {
//                                Text("c. Customer Installation Responsibility. ")
//                            }
//                            Text(" It is the sole responsibility of the Customer to install\n" +
//                                    "all Efficiency Measures in a timely manner. Customer agrees to provide Green Rebates with all\n" +
//                                    "information necessary for the submission, approval, and completion of the rebate project with\n" +
//                                    "the Service Provider.")
//                        }
//                        P(Modifier.margin(0.px).toAttrs()) {
//                            Span(Modifier.fontWeight(FontWeight.Bold).margin(0.px).toAttrs()) {
//                                Text("d. Payment Terms. ")
//                            }
//                            Text("Payment terms are outlined in the Financing section of this document.\n" +
//                                    "Deposit is to be paid prior to shipment of Efficiency Measures, and monthly payments are\n" +
//                                    "to be made on the first of each subsequent month. There are no penalties for early payment.\n" +
//                                    "If any payment is not paid when due, interest will be added to and payable on all overdue amounts\n" +
//                                    "at twenty-four percent (24%) per annum, or the maximum percentage allowed under applicable\n" +
//                                    "laws, whichever is less.")
//                        }
//                        P(Modifier.margin(top = 20.px).toAttrs()) {
//                            Text("Customer Signature:")
//                        }
//                        P(Modifier.margin(top = 20.px).toAttrs()) {
//                            Text("Date:")
//                        }
//                    }
//                    pageFooter(pageNumbers["termsconditions"]!!, pageTotal, GeneratorData.generalInputsState.value.projectName)
//                }
//            }
        }
    }
}

@Composable
fun pageFooter(count: Int, total: Int, projectName: String) {
    Div(Modifier.fillMaxWidth().position(Position.Absolute).bottom(-1.px).margin(left = -1.px).toAttrs()) {
        Row(Modifier.fillMaxWidth().gap(50.px).color(Colors.Grey).fontSize(12.px).fontFamily(FONT_FAMILY2), horizontalArrangement = Arrangement.Center) {
            P(attrs = Modifier.margin(5.px).toAttrs()) {
                Text("Proposal: ${getCurrentDateString()}-$projectName")
            }
            P(attrs = Modifier.margin(5.px).toAttrs()) {
                Text("Page $count of $total")
            }
        }
        Div(pageFooterStyle.toModifier().toAttrs()) {}
    }
}

@Composable
fun sectionTitle(title: String) {
    Div(sectionTitleGradiantStyle.toModifier().toAttrs()) {
        H2(Modifier.textTransform(TextTransform.Uppercase).fontSize(52.px).fontWeight(FontWeight.Bold).letterSpacing(2.3.px).toAttrs()) {
            Text(title)
        }
    }
}

@Composable
fun summarySectionTitle(title: String) {
    Div(smallSectionTitleGradiantStyle.toModifier().backgroundColor(Theme.PDFNearBackground.rgb).toAttrs()) {
        H3(Modifier.textTransform(TextTransform.Uppercase).fontFamily(FONT_FAMILY2).fontSize(16.px).fontWeight(FontWeight.Bold).margin(0.px).toAttrs()) {
            Text(title)
        }
    }
}

@Composable
fun formulaSectionTitle(title: String) {
    Div(Modifier.display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).toAttrs()) {
        H3(Modifier.fillMaxWidth().padding(bottom = 5.px).margin(bottom = 5.px).borderBottom(1.px, LineStyle.Solid, Colors.LightGray).color(Theme.PDFNearBackground.rgb).fontSize(20.px).fontFamily(FONT_FAMILY2).fontWeight(
            FontWeight.Bolder).toAttrs()) {
            Text(title)
        }
    }
}

@Composable
fun formulaLine(label: String, formula: String) {
    Row(Modifier.fontSize(11.px).fontFamily(FONT_FAMILY2)) {
        Span(Modifier.width(350.px).toAttrs()) {
            Text(label)
        }
        Span(Modifier.width(30.px).toAttrs()) {
            Text(" = ")
        }
        Span(Modifier.width(800.px).toAttrs()) {
            Text(formula)
        }
    }
}

@Composable
fun pdfSummaryBox(label: String, value: Any) {
    Div(Modifier.fontFamily(FONT_FAMILY2).display(DisplayStyle.Flex).flexDirection(FlexDirection.Column).alignItems(AlignItems.Start)
        .justifyContent(JustifyContent.Center).color(Colors.White).toAttrs()) {
        Span(Modifier.fontSize(13.3.px).fontWeight(FontWeight.Bold).margin(bottom = -4.px).toAttrs()) {
            Text(label)
        }
        Span(Modifier.fontSize(24.px).fontWeight(FontWeight.Bold).toAttrs()) {
            Text(value.toString())
        }
    }
}